import React from 'react';
import { useTranslation } from 'react-i18next';

const DOCUMENT_ZOOM_DEFAULT = 100;
const DOCUMENT_ZOOM_MIN = 1;
const DOCUMENT_ZOOM_MAX = 500;
const DOCUMENT_ZOOM_WHEEL_STEP = 10;

function clampZoom(value, min = DOCUMENT_ZOOM_MIN, max = DOCUMENT_ZOOM_MAX) {
  return Math.max(min, Math.min(max, value));
}

function DocumentDetailsModal({
  selectedDocument,
  documentViewer,
  documentForm,
  onDocumentFormChange,
  isDocumentSaving,
  documentSaveError,
  documentSaveSuccess,
  isDocumentDownloading = false,
  documentDownloadError = '',
  documentStatusOptions,
  onDownload,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(DOCUMENT_ZOOM_DEFAULT);
  const [fitZoom, setFitZoom] = React.useState(DOCUMENT_ZOOM_DEFAULT);
  const [viewportSize, setViewportSize] = React.useState({ width: 0, height: 0 });
  const [imageNaturalSize, setImageNaturalSize] = React.useState({ width: 0, height: 0 });
  const [hasUserZoom, setHasUserZoom] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const viewportRef = React.useRef(null);
  const statusDropdownRef = React.useRef(null);
  const zoomRef = React.useRef(DOCUMENT_ZOOM_DEFAULT);
  const dragRef = React.useRef({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const zoomAnchorRef = React.useRef(null);
  const isImagePreview = Boolean(
    !documentViewer.isLoading
    && !documentViewer.error
    && documentViewer.url
    && String(documentViewer.mime || '').startsWith('image/'),
  );
  const isPdfPreview = Boolean(
    !documentViewer.isLoading
    && !documentViewer.error
    && documentViewer.url
    && String(documentViewer.mime || '').includes('pdf'),
  );
  const zoomMin = isImagePreview ? fitZoom : DOCUMENT_ZOOM_MIN;
  const zoomMax = DOCUMENT_ZOOM_MAX;
  const imageRenderWidth = imageNaturalSize.width ? Math.round((imageNaturalSize.width * zoom) / 100) : 0;
  const imageRenderHeight = imageNaturalSize.height ? Math.round((imageNaturalSize.height * zoom) / 100) : 0;

  React.useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  React.useEffect(() => {
    setZoom((prev) => clampZoom(prev, zoomMin, zoomMax));
  }, [zoomMax, zoomMin]);

  React.useEffect(() => {
    setIsFullscreen(false);
    setZoom(DOCUMENT_ZOOM_DEFAULT);
    setFitZoom(DOCUMENT_ZOOM_DEFAULT);
    setViewportSize({ width: 0, height: 0 });
    setImageNaturalSize({ width: 0, height: 0 });
    setHasUserZoom(false);
    setIsDragging(false);
    setIsStatusMenuOpen(false);
  }, [selectedDocument?.uid, selectedDocument?.id]);

  React.useEffect(() => {
    if (!isStatusMenuOpen) {
      return undefined;
    }

    const handleMouseDown = (event) => {
      if (statusDropdownRef.current?.contains(event.target)) {
        return;
      }
      setIsStatusMenuOpen(false);
    };

    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [isStatusMenuOpen]);

  React.useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return undefined;
    }

    const updateSize = () => {
      const rect = viewportElement.getBoundingClientRect();
      setViewportSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(viewportElement);

    return () => observer.disconnect();
  }, [isFullscreen]);

  React.useEffect(() => {
    if (documentViewer.isLoading || !isImagePreview) {
      return;
    }

    if (!imageNaturalSize.width || !imageNaturalSize.height || !viewportSize.width || !viewportSize.height) {
      return;
    }

    const widthRatio = viewportSize.width / imageNaturalSize.width;
    const heightRatio = viewportSize.height / imageNaturalSize.height;
    const fitted = Math.min(widthRatio, heightRatio, 1);
    const fittedPercent = Math.max(DOCUMENT_ZOOM_MIN, Math.floor(fitted * 100));

    setFitZoom(fittedPercent);
    if (!hasUserZoom) {
      setZoom(fittedPercent);
    }
  }, [
    documentViewer.isLoading,
    hasUserZoom,
    imageNaturalSize.height,
    imageNaturalSize.width,
    isImagePreview,
    viewportSize.height,
    viewportSize.width,
  ]);

  React.useEffect(() => {
    if (!isImagePreview) {
      setFitZoom(DOCUMENT_ZOOM_DEFAULT);
      setZoom(DOCUMENT_ZOOM_DEFAULT);
      setHasUserZoom(false);
    }
  }, [isImagePreview]);

  React.useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const viewportElement = viewportRef.current;
      if (!viewportElement) {
        return;
      }

      const deltaX = event.clientX - dragRef.current.startX;
      const deltaY = event.clientY - dragRef.current.startY;
      viewportElement.scrollLeft = dragRef.current.scrollLeft - deltaX;
      viewportElement.scrollTop = dragRef.current.scrollTop - deltaY;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleResetZoom = React.useCallback(() => {
    const viewportElement = viewportRef.current;
    const prevZoom = zoomRef.current;
    const nextZoom = clampZoom(isImagePreview ? fitZoom : DOCUMENT_ZOOM_DEFAULT, zoomMin, zoomMax);
    if (!viewportElement || nextZoom === prevZoom) {
      setHasUserZoom(false);
      setZoom(nextZoom);
      return;
    }

    const viewportWidth = viewportElement.clientWidth;
    const viewportHeight = viewportElement.clientHeight;
    const centerXInContent = viewportElement.scrollLeft + (viewportWidth / 2);
    const centerYInContent = viewportElement.scrollTop + (viewportHeight / 2);

    if (isImagePreview && imageNaturalSize.width > 0 && imageNaturalSize.height > 0) {
      const prevRenderWidth = Math.round((imageNaturalSize.width * prevZoom) / 100);
      const prevRenderHeight = Math.round((imageNaturalSize.height * prevZoom) / 100);
      const prevContentWidth = Math.max(prevRenderWidth, viewportWidth);
      const prevContentHeight = Math.max(prevRenderHeight, viewportHeight);
      const prevImageLeftInContent = (prevContentWidth - prevRenderWidth) / 2;
      const prevImageTopInContent = (prevContentHeight - prevRenderHeight) / 2;
      const imagePointX = centerXInContent - prevImageLeftInContent;
      const imagePointY = centerYInContent - prevImageTopInContent;
      const ratio = nextZoom / prevZoom;

      const nextRenderWidth = Math.round((imageNaturalSize.width * nextZoom) / 100);
      const nextRenderHeight = Math.round((imageNaturalSize.height * nextZoom) / 100);
      const nextContentWidth = Math.max(nextRenderWidth, viewportWidth);
      const nextContentHeight = Math.max(nextRenderHeight, viewportHeight);
      const nextImageLeftInContent = (nextContentWidth - nextRenderWidth) / 2;
      const nextImageTopInContent = (nextContentHeight - nextRenderHeight) / 2;

      zoomAnchorRef.current = {
        type: 'image-center',
        targetLeft: (nextImageLeftInContent + (imagePointX * ratio)) - (viewportWidth / 2),
        targetTop: (nextImageTopInContent + (imagePointY * ratio)) - (viewportHeight / 2),
      };
    } else {
      const ratio = nextZoom / prevZoom;
      zoomAnchorRef.current = {
        type: 'content-center',
        targetLeft: (centerXInContent * ratio) - (viewportWidth / 2),
        targetTop: (centerYInContent * ratio) - (viewportHeight / 2),
      };
    }

    setHasUserZoom(false);
    zoomRef.current = nextZoom;
    setZoom(nextZoom);
  }, [fitZoom, imageNaturalSize.height, imageNaturalSize.width, isImagePreview, zoomMax, zoomMin]);

  const handleViewportWheel = React.useCallback((event) => {
    if (!(isImagePreview || isPdfPreview)) {
      return;
    }

    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const prevZoom = zoomRef.current;
    const nextZoom = clampZoom(prevZoom + (event.deltaY < 0 ? DOCUMENT_ZOOM_WHEEL_STEP : -DOCUMENT_ZOOM_WHEEL_STEP), zoomMin, zoomMax);
    if (nextZoom === prevZoom) {
      return;
    }

    const viewportWidth = viewportElement.clientWidth;
    const viewportHeight = viewportElement.clientHeight;
    const centerXInContent = viewportElement.scrollLeft + (viewportWidth / 2);
    const centerYInContent = viewportElement.scrollTop + (viewportHeight / 2);

    if (isImagePreview && imageNaturalSize.width > 0 && imageNaturalSize.height > 0) {
      const prevRenderWidth = Math.round((imageNaturalSize.width * prevZoom) / 100);
      const prevRenderHeight = Math.round((imageNaturalSize.height * prevZoom) / 100);
      const prevContentWidth = Math.max(prevRenderWidth, viewportWidth);
      const prevContentHeight = Math.max(prevRenderHeight, viewportHeight);
      const prevImageLeftInContent = (prevContentWidth - prevRenderWidth) / 2;
      const prevImageTopInContent = (prevContentHeight - prevRenderHeight) / 2;
      const imagePointX = centerXInContent - prevImageLeftInContent;
      const imagePointY = centerYInContent - prevImageTopInContent;
      const ratio = nextZoom / prevZoom;

      const nextRenderWidth = Math.round((imageNaturalSize.width * nextZoom) / 100);
      const nextRenderHeight = Math.round((imageNaturalSize.height * nextZoom) / 100);
      const nextContentWidth = Math.max(nextRenderWidth, viewportWidth);
      const nextContentHeight = Math.max(nextRenderHeight, viewportHeight);
      const nextImageLeftInContent = (nextContentWidth - nextRenderWidth) / 2;
      const nextImageTopInContent = (nextContentHeight - nextRenderHeight) / 2;

      zoomAnchorRef.current = {
        type: 'image-center',
        targetLeft: (nextImageLeftInContent + (imagePointX * ratio)) - (viewportWidth / 2),
        targetTop: (nextImageTopInContent + (imagePointY * ratio)) - (viewportHeight / 2),
      };
    } else {
      const ratio = nextZoom / prevZoom;
      zoomAnchorRef.current = {
        type: 'content-center',
        targetLeft: (centerXInContent * ratio) - (viewportWidth / 2),
        targetTop: (centerYInContent * ratio) - (viewportHeight / 2),
      };
    }

    setHasUserZoom(true);
    zoomRef.current = nextZoom;
    setZoom(nextZoom);
  }, [imageNaturalSize.height, imageNaturalSize.width, isImagePreview, isPdfPreview, zoomMax, zoomMin]);

  React.useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return undefined;
    }

    const nativeWheelHandler = (event) => {
      handleViewportWheel(event);
    };

    viewportElement.addEventListener('wheel', nativeWheelHandler, { passive: false });
    return () => viewportElement.removeEventListener('wheel', nativeWheelHandler);
  }, [handleViewportWheel]);

  React.useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const viewportElement = viewportRef.current;
    if (!anchor || !viewportElement) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      const targetLeft = anchor.targetLeft;
      const targetTop = anchor.targetTop;

      const maxScrollLeft = Math.max(0, viewportElement.scrollWidth - viewportElement.clientWidth);
      const maxScrollTop = Math.max(0, viewportElement.scrollHeight - viewportElement.clientHeight);

      viewportElement.scrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);
      viewportElement.scrollTop = Math.min(Math.max(targetTop, 0), maxScrollTop);
      zoomAnchorRef.current = null;
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [imageNaturalSize.height, imageNaturalSize.width, imageRenderHeight, imageRenderWidth, zoom]);

  const canPanX = isImagePreview
    && imageNaturalSize.width > 0
    && viewportSize.width > 0
    && ((imageNaturalSize.width * zoom) / 100 > viewportSize.width);
  const canPanY = isImagePreview
    && imageNaturalSize.height > 0
    && viewportSize.height > 0
    && ((imageNaturalSize.height * zoom) / 100 > viewportSize.height);
  const canPanImage = canPanX || canPanY;

  const handleViewportMouseDown = React.useCallback((event) => {
    if (!canPanImage || event.button !== 0) {
      return;
    }

    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return;
    }

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: viewportElement.scrollLeft,
      scrollTop: viewportElement.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  }, [canPanImage]);

  const defaultZoom = isImagePreview ? fitZoom : DOCUMENT_ZOOM_DEFAULT;
  const shouldShowZoomBadge = isImagePreview
    ? (fitZoom < 100 || zoom !== defaultZoom)
    : zoom !== defaultZoom;
  const documentTypeLabel = String(selectedDocument?.type || '').trim() || t('documents.documentDetails');
  const documentStatusLabel = String(documentForm.status || selectedDocument?.status || '').trim() || 'Unknown';

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className={`modal-dialog ${isFullscreen ? 'modal-fullscreen' : 'modal-lg'}`}>
          <div
            className="modal-content"
            style={isFullscreen ? { height: '100vh', overflow: 'hidden' } : undefined}
          >
            <div className="modal-header app-document-details-modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <span className="badge text-uppercase bg-primary-subtle text-primary">{documentTypeLabel}</span>
              </h5>
              <div className="d-flex align-items-center gap-2 app-document-header-actions">
                <div className="dropdown app-document-status-dropdown" ref={statusDropdownRef}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                    aria-expanded={isStatusMenuOpen}
                    onClick={() => setIsStatusMenuOpen((prev) => !prev)}
                    disabled={isDocumentSaving}
                  >
                    {documentStatusLabel}
                  </button>
                  <div className={`dropdown-menu dropdown-menu-dark app-document-status-menu ${isStatusMenuOpen ? 'show' : ''}`}>
                    {documentStatusOptions.map((statusValue) => (
                      <button
                        key={statusValue}
                        type="button"
                        className={`dropdown-item ${documentForm.status === statusValue ? 'active' : ''}`}
                        onClick={() => {
                          onDocumentFormChange({ status: statusValue });
                          setIsStatusMenuOpen(false);
                        }}
                      >
                        {statusValue}
                      </button>
                    ))}
                  </div>
                </div>
                {onDownload ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    onClick={onDownload}
                    disabled={isDocumentDownloading}
                    title={t('documents.download')}
                  >
                    <i className="ti ti-download" aria-hidden="true" />
                    <span>{isDocumentDownloading ? t('documents.downloading') : t('documents.download')}</span>
                  </button>
                ) : null}
                <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={isDocumentSaving}>
                  {isDocumentSaving ? t('metrics.saving') : t('common.save')}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary app-document-header-icon-btn"
                  onClick={() => setIsFullscreen((prev) => !prev)}
                  title={isFullscreen ? t('documents.defaultSize') : t('documents.fullscreen')}
                  aria-label={isFullscreen ? t('documents.defaultSize') : t('documents.fullscreen')}
                >
                  <i className={`ti ${isFullscreen ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
                </button>
                <button type="button" className="btn-close" onClick={onClose}></button>
              </div>
            </div>
            <div
              className={`modal-body app-document-details-modal-body ${isFullscreen ? 'app-document-details-modal-body-fullscreen' : ''}`}
            >
              <div
                className="border rounded app-document-preview-surface app-document-details-preview position-relative"
              >
                {shouldShowZoomBadge ? (
                  <div className="position-absolute top-0 start-50 translate-middle-x p-2" style={{ zIndex: 2 }}>
                    <span className="badge text-bg-dark d-inline-flex align-items-center gap-2">
                      {zoom}%
                      <button type="button" className="btn btn-sm btn-light py-0 px-2" onClick={handleResetZoom}>
                        {t('documents.resetZoom')}
                      </button>
                    </span>
                  </div>
                ) : null}
                <div
                  ref={viewportRef}
                  className="overflow-auto app-document-preview-viewport"
                  style={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    cursor: canPanImage ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    overflowX: canPanX ? 'auto' : 'hidden',
                    overflowY: canPanY ? 'auto' : 'hidden',
                  }}
                  onMouseDown={handleViewportMouseDown}
                >
                  <div
                    style={{
                      width: canPanX ? `${imageRenderWidth}px` : '100%',
                      height: canPanY ? `${imageRenderHeight}px` : '100%',
                      display: 'flex',
                      alignItems: canPanY ? 'flex-start' : 'center',
                      justifyContent: canPanX ? 'flex-start' : 'center',
                    }}
                  >
                    {documentViewer.isLoading ? <span className="text-muted">{t('profiles.loadingPreview')}</span> : null}
                    {!documentViewer.isLoading && documentViewer.error ? <span className="text-danger">{documentViewer.error}</span> : null}
                    {!documentViewer.isLoading && !documentViewer.error && documentViewer.url && String(documentViewer.mime || '').startsWith('image/') ? (
                      <img
                        src={documentViewer.url}
                        alt={selectedDocument?.name || selectedDocument?.type || 'Document'}
                        onLoad={(event) => {
                          setImageNaturalSize({
                            width: event.currentTarget.naturalWidth || 0,
                            height: event.currentTarget.naturalHeight || 0,
                          });
                        }}
                        onDragStart={(event) => event.preventDefault()}
                        style={{
                          display: 'block',
                          width: imageNaturalSize.width ? `${imageRenderWidth}px` : 'auto',
                          height: imageNaturalSize.height ? `${imageRenderHeight}px` : 'auto',
                          maxWidth: 'none',
                        }}
                      />
                    ) : null}
                    {!documentViewer.isLoading && !documentViewer.error && documentViewer.url && String(documentViewer.mime || '').includes('pdf') ? (
                      <iframe
                        title="Document preview"
                        src={documentViewer.url}
                        style={{ width: `${zoom}%`, minWidth: '100%', minHeight: `${Math.round((420 * zoom) / 100)}px`, border: 0 }}
                      />
                    ) : null}
                    {!documentViewer.isLoading
                      && !documentViewer.error
                      && (!documentViewer.url || (!String(documentViewer.mime || '').startsWith('image/') && !String(documentViewer.mime || '').includes('pdf'))) ? (
                        <span className="text-muted">{t('documents.previewUnavailableFormat')}</span>
                    ) : null}
                  </div>
                </div>
                <div className="px-3 pb-2 pt-2 app-document-preview-surface border-top">
                  <input
                    type="range"
                    className="form-range m-0"
                    min={zoomMin}
                    max={zoomMax}
                    step={1}
                    value={zoom}
                    onChange={(event) => {
                      const nextZoom = clampZoom(Number(event.target.value), zoomMin, zoomMax);
                      const viewportElement = viewportRef.current;
                      const prevZoom = zoomRef.current;
                      if (viewportElement && nextZoom !== prevZoom) {
                        const viewportWidth = viewportElement.clientWidth;
                        const viewportHeight = viewportElement.clientHeight;
                        const centerXInContent = viewportElement.scrollLeft + (viewportWidth / 2);
                        const centerYInContent = viewportElement.scrollTop + (viewportHeight / 2);

                        if (isImagePreview && imageNaturalSize.width > 0 && imageNaturalSize.height > 0) {
                          const prevRenderWidth = Math.round((imageNaturalSize.width * prevZoom) / 100);
                          const prevRenderHeight = Math.round((imageNaturalSize.height * prevZoom) / 100);
                          const prevContentWidth = Math.max(prevRenderWidth, viewportWidth);
                          const prevContentHeight = Math.max(prevRenderHeight, viewportHeight);
                          const prevImageLeftInContent = (prevContentWidth - prevRenderWidth) / 2;
                          const prevImageTopInContent = (prevContentHeight - prevRenderHeight) / 2;
                          const imagePointX = centerXInContent - prevImageLeftInContent;
                          const imagePointY = centerYInContent - prevImageTopInContent;
                          const ratio = nextZoom / prevZoom;

                          const nextRenderWidth = Math.round((imageNaturalSize.width * nextZoom) / 100);
                          const nextRenderHeight = Math.round((imageNaturalSize.height * nextZoom) / 100);
                          const nextContentWidth = Math.max(nextRenderWidth, viewportWidth);
                          const nextContentHeight = Math.max(nextRenderHeight, viewportHeight);
                          const nextImageLeftInContent = (nextContentWidth - nextRenderWidth) / 2;
                          const nextImageTopInContent = (nextContentHeight - nextRenderHeight) / 2;

                          zoomAnchorRef.current = {
                            type: 'image-center',
                            targetLeft: (nextImageLeftInContent + (imagePointX * ratio)) - (viewportWidth / 2),
                            targetTop: (nextImageTopInContent + (imagePointY * ratio)) - (viewportHeight / 2),
                          };
                        } else {
                          const ratio = nextZoom / prevZoom;
                          zoomAnchorRef.current = {
                            type: 'content-center',
                            targetLeft: (centerXInContent * ratio) - (viewportWidth / 2),
                            targetTop: (centerYInContent * ratio) - (viewportHeight / 2),
                          };
                        }
                      }

                      setHasUserZoom(true);
                      zoomRef.current = nextZoom;
                      setZoom(nextZoom);
                    }}
                  />
                </div>
              </div>

              <div className="row g-3 mb-3 app-document-details-form-row">
                <div className="col-7">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label mb-0">{t('documents.translation')}</label>
                    <div className="form-check m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="document-translation-pending"
                        checked={Boolean(documentForm.translationRequired)}
                        onChange={(event) => onDocumentFormChange({ translationRequired: event.target.checked })}
                        disabled={isDocumentSaving}
                      />
                      <label className="form-check-label small ms-1" htmlFor="document-translation-pending">
                        {t('documents.translationPending')}
                      </label>
                    </div>
                  </div>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={documentForm.translation}
                    onChange={(event) => onDocumentFormChange({ translation: event.target.value })}
                    disabled={isDocumentSaving}
                  />
                </div>
                <div className="col-5">
                  <label className="form-label">{t('documents.expirationDate')}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={documentForm.expirationDate}
                    onChange={(event) => onDocumentFormChange({ expirationDate: event.target.value })}
                    disabled={isDocumentSaving}
                  />
                </div>
              </div>

              {documentSaveError ? <div className="alert alert-danger py-2">{documentSaveError}</div> : null}
              {documentDownloadError ? <div className="alert alert-danger py-2">{documentDownloadError}</div> : null}
              {documentSaveSuccess ? <div className="alert alert-success py-2">{documentSaveSuccess}</div> : null}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default DocumentDetailsModal;
